import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileCheck,
  Search,
  MapPin,
  Phone,
  ExternalLink,
  Clock,
  Building2,
  Loader
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import notaryOfficesService from '../services/notaryOfficesService';
import PermissionGate from '../components/PermissionGate';
import './NotaryOfficesPage.css';

const NotaryOfficesPage = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);

  // Fetch notary offices from API
  const fetchOffices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await notaryOfficesService.getAll();
      if (response.success) {
        setOffices(response.data.offices || response.data || []);
      } else {
        setError(response.message || 'Failed to fetch offices');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch offices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOffices();
  }, [fetchOffices]);

  const filteredOffices = offices.filter(office =>
    (office.isActive !== false) && (
      (office.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (office.location || '').toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const handleWhatsAppClick = (whatsapp) => {
    window.open(`https://wa.me/${whatsapp}`, '_blank');
  };

  const handleCallClick = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  const handleLinkClick = (link) => {
    window.open(link, '_blank');
  };

  if (loading) {
    return (
      <div className="notary-offices-page">
        <div className="loading-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Loader className="spin" size={40} />
        </div>
      </div>
    );
  }

  return (
    <PermissionGate feature="PAGE_NOTARY" type="page">
    <div className="notary-offices-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-icon">
            <FileCheck size={28} />
          </div>
          <div className="header-text">
            <h1>{t('notaryOffices.title', 'مكاتب التوثيق')}</h1>
            <p>{t('notaryOffices.subtitle', 'تصفح مكاتب التوثيق المعتمدة وتواصل معهم مباشرة')}</p>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="search-section">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder={t('notaryOffices.searchPlaceholder', 'ابحث عن مكتب توثيق...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="results-count">
          {filteredOffices.length} {t('notaryOffices.office', 'مكتب')}
        </div>
      </div>

      {/* Offices Grid */}
      <div className="offices-grid">
        {filteredOffices.map((office) => (
          <div key={office.id} className="office-card">
            <div className="office-image">
              {office.image ? (
                <img src={office.image} alt={office.name} />
              ) : (
                <div className="image-placeholder">
                  <Building2 size={48} />
                </div>
              )}
            </div>
            
            <div className="office-content">
              <h3 className="office-name">{office.name}</h3>
              <p className="office-description">{office.description}</p>
              
              <div className="office-details">
                <div className="detail-item">
                  <MapPin size={16} />
                  <span>{office.location}</span>
                </div>
                <div className="detail-item">
                  <Clock size={16} />
                  <span>{office.workingHours}</span>
                </div>
                <div className="detail-item">
                  <Phone size={16} />
                  <span dir="ltr">{office.phone}</span>
                </div>
              </div>

              <div className="office-actions">
                <button 
                  className="action-btn whatsapp"
                  onClick={() => handleWhatsAppClick(office.whatsapp)}
                  title={t('notaryOffices.whatsapp', 'واتساب')}
                >
                  <FaWhatsapp size={20} />
                  <span>{t('notaryOffices.whatsapp', 'واتساب')}</span>
                </button>
                
                <button 
                  className="action-btn call"
                  onClick={() => handleCallClick(office.phone)}
                  title={t('notaryOffices.call', 'اتصال')}
                >
                  <Phone size={20} />
                  <span>{t('notaryOffices.call', 'اتصال')}</span>
                </button>
                
                {office.link && (
                  <button 
                    className="action-btn link"
                    onClick={() => handleLinkClick(office.link)}
                    title={t('notaryOffices.visitWebsite', 'زيارة الموقع')}
                  >
                    <ExternalLink size={20} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredOffices.length === 0 && (
        <div className="empty-state">
          <FileCheck size={64} />
          <h3>{t('notaryOffices.noResults', 'لا توجد نتائج')}</h3>
          <p>{t('notaryOffices.noResultsDesc', 'لم نجد مكاتب توثيق مطابقة لبحثك')}</p>
        </div>
      )}
    </div>
    </PermissionGate>
  );
};

export default NotaryOfficesPage;
